import { getQueryKey } from '@trpc/react-query';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { capitalize } from 'lodash';
import {
  OnboardingStepStatus,
  OnboardingStep,
} from 'further-types/lp/onboarding';
import { LpEmailTemplate } from 'further-types/lp/communications';
import { Column, Row } from 'further-ui/components/Layout';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import Avatar from 'further-ui/components/Avatar';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import Banner from 'components/new/Banner';
import Button from 'components/Button';
import { pluralize } from 'further-ui/utils';
import { VehicleApplication, InvestorUser } from '../types';

const SendAllButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.lg),
}));

const AvatarBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    height: '12px',
    minWidth: '12px',
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: '50%',
  },
}));

type Props = {
  vehicleApplicationId: string;
  vehicleId: string;
  application: VehicleApplication;
  users: Array<InvestorUser>;
  showSystemEmailsBanner?: boolean;
};

const Communications: React.FC<Props> = ({
  vehicleId,
  vehicleApplicationId,
  application,
  users,
  showSystemEmailsBanner,
}) => {
  const handleTrpcMutation = useHandleTrpcMutation();
  const queryKey = getQueryKey(
    api.onboarding.investorDetails,
    { vehicleId, vehicleApplicationId },
    'query',
  );
  const { mutate: sendCommunications, isPending } =
    api.vehicleApplication.sendCommunications.useMutation();

  const hasNoRequests = application.applicationSteps
    .filter(
      (step) =>
        step.step !== OnboardingStep.InvestorPortalView &&
        step.step !== OnboardingStep.DataRoomView,
    )
    .every((step) => step.status === OnboardingStepStatus.Disabled);
  const hasNoCommitment = !application.commitment;
  const { isManuallyAddedCommitment } = application.commitmentSettings;
  const usersToSendEmail = users.filter(
    (user) => user.copyIntoSystemEmails && !user.firstInvite,
  );
  const availableEmailCount = usersToSendEmail.length;
  const currentEmail = hasNoCommitment
    ? LpEmailTemplate.OnboardingTasksEmail
    : isManuallyAddedCommitment
    ? LpEmailTemplate.PostSubscriptionInvitation
    : LpEmailTemplate.OnboardingSubscriptionSuccessfullyCompletedEmail;
  const emailName = hasNoCommitment
    ? 'onboarding task'
    : isManuallyAddedCommitment
    ? 'post-subscription'
    : 'completed subscription';
  const emailType = hasNoCommitment ? 'email' : 'invitation';

  const columns: Array<TableColumn<(typeof users)[number]>> = [
    {
      label: 'User',
      key: 'user',
      render: (user) => {
        return (
          <Row spacing="xs" centered>
            <AvatarBadge
              variant="dot"
              color="warning"
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              invisible={
                (hasNoRequests && hasNoCommitment) ||
                !user.copyIntoSystemEmails ||
                !!user.firstInvite
              }
            >
              <Avatar
                color="random"
                text={`${user.firstName} ${user.lastName}`}
                size="lg"
              />
            </AvatarBadge>
            <Column>
              <Typography variant="h6">
                {user.firstName ?? ''} {user.lastName ?? ''}
              </Typography>
              <Typography variant="body2">{user.email} </Typography>
            </Column>
          </Row>
        );
      },
    },
    {
      label: 'First invite',
      key: 'firstInvite',
      render: (user) => user.firstInvite?.toReadable() ?? '--',
    },
    {
      label: 'Last email',
      key: 'lastEmail',
      render: (user) => user.lastEmail?.toReadable() ?? '--',
    },
    {
      label: 'Last login',
      key: 'lastLogin',
      render: (user) => user.lastLogin?.toReadable() ?? '--',
    },
  ];

  return (
    <Column spacing="lg">
      {showSystemEmailsBanner ? (
        <Row spacing="sm">
          <Banner
            message={`Send ${availableEmailCount} ${emailName} ${pluralize(
              emailType,
              availableEmailCount,
            )}`}
            requiresAction
          />
          <SendAllButton
            variant="contained"
            color="info"
            whiteSpace="nowrap"
            loading={isPending}
            onClick={() => {
              sendCommunications(
                {
                  vehicleId,
                  vehicleApplicationId,
                  furtherUserIds: usersToSendEmail.map((user) => user.id),
                  communication: currentEmail,
                },
                handleTrpcMutation({
                  successMessage: `${availableEmailCount} ${emailName} ${pluralize(
                    emailType,
                    availableEmailCount,
                  )} sent successfully.`,
                  invalidations: [queryKey],
                }),
              );
            }}
          >
            Send to all
          </SendAllButton>
        </Row>
      ) : null}
      <Table
        columns={columns}
        rows={users}
        isDisabledRow={(user) =>
          (hasNoRequests && hasNoCommitment) || !user.copyIntoSystemEmails
        }
        disabledRowTooltip={(user) => (
          <>
            {hasNoRequests && hasNoCommitment
              ? 'You cannot invite this user to the portal while the investor has no onboarding requests or commitments in the system. At least one of these items is required to invite the user.'
              : null}
            {hasNoRequests && hasNoCommitment && !user.copyIntoSystemEmails ? (
              <>
                <br />
                <br />
              </>
            ) : null}
            {!user.copyIntoSystemEmails
              ? 'Users with emails toggled off in their settings cannot be sent communications.'
              : null}
          </>
        )}
        isLoading={false}
        rowsTotalCount={users.length}
        disableSorting
        actionsHandleIcon={() => (
          <Tooltip title="Click to view sending options">
            <EmailOutlined fontSize="small" />
          </Tooltip>
        )}
        actions={[
          {
            label: (user) => {
              const emailSent = application.invitationEmails.find(
                (email) =>
                  email.email === currentEmail &&
                  email.furtherUserId === user.id,
              );
              return `${
                emailSent ? 'Resend' : 'Send'
              } ${emailName} ${emailType}`;
            },
            icon: EmailOutlined,
            onClick: (user) => {
              sendCommunications(
                {
                  vehicleId,
                  vehicleApplicationId,
                  furtherUserIds: [user.id],
                  communication: currentEmail,
                },
                handleTrpcMutation({
                  successMessage: `${capitalize(
                    emailName,
                  )} ${emailType} sent successfully.`,
                  invalidations: [queryKey],
                }),
              );
            },
            disabled: (user) => !user.copyIntoSystemEmails || isPending,
            requiresAction: (user) => !user.firstInvite,
          },
        ]}
        emptyMessage={
          <Typography variant="body2" textAlign="center" color="grey.500">
            No users have been added to this investor yet.
            <br />
            Go to the Investor details tab to add users before the investor can
            be invited to the system.
          </Typography>
        }
      />
    </Column>
  );
};

export default Communications;
